<template>
  <PartnersPostContainer>
    <template #preventPageTitle>
      {{ post?.title?.rendered || '' }}
    </template>

    <template #banner>
      <img v-if="postStatus === 'success'" :src="post?.acf?.partners_image || '/images/topPost.webp'">
    </template>

    <template #postTitle>
      {{ post?.title?.rendered || '' }}
    </template>

    <template #content>
      <div 
        v-if="post?.content?.rendered"
        v-html="post.content.rendered"
      />
    </template>

    <template #promo>
      <PartnersPromo
        v-if="post?.acf?.promo?.promocode"
        :code="post.acf.promo.promocode"
        :value="post.acf.promo.promo_value"
        :type="post.acf.promo.promo_type"
        :caption="post.acf.promo.promo_caption"
      />
    </template>

    <template
      #features
      v-if="post?.acf?.features"
    >
      <div :class="$style.featTitle">{{ t('partners.features') }}</div>
      <div :class="$style.featList">
        <ul>
          <li
            v-for="(feat, index) of post.acf.features.split('\r\n')"
            :key="index"
          >{{ feat }}</li>
        </ul>
      </div>

      <UiButton :to="post?.acf?.partners_website">
        <template #label>
          {{ t('partners.website') }}
        </template>

        <template #preppendIcon>
          <UiIconsArrowTopRight />
        </template>
      </UiButton>
    </template>

    <template #recent>
      <PartnersRecent
        :loading="recentPostLoader"
        :posts="recentPosts"
      />
    </template>
  </PartnersPostContainer>
</template>

<script setup lang="ts">
import {
  fetchWpList,
  getPostLang,
  resolveLocalizedPost,
} from '~/utils/wordpress';

const route = useRoute();
const { locale, t } = useI18n();
const localePath = useLocalePath();

const getPost = async (): Promise<Record<any, any> | null> => {
  return resolveLocalizedPost({
    id: String(route.params._id),
    locale: locale.value,
    localizedPath: id => localePath(`/partners/${id}`),
    responseFormat: 'wp',
    detailEndpoint: 'wp/v2/partners',
    listEndpoint: 'wp/v2/partners',
  });
};

const getRecentPosts = async (postId?: string | number): Promise<Record<any, any>[]> => {
  if (!postId) {
    return [];
  }

  try {
    const result = await fetchWpList('wp/v2/partners', {
      meta_key: 'lang',
      meta_value: locale.value,
      exclude: String(postId),
      per_page: '3',
    });

    return result.slice(0, 3);
  } catch (error) {
    console.error(error);
    return [];
  }
};

const { data: post, status: postStatus } = useAsyncData(
  `partner-post-${locale.value}-${route.params._id}`,
  () => getPost(),
  {
    server: true,
    lazy: false,
    default: () => null,
  },
);

const { data: recentPosts, status: recentPostLoader } = useAsyncData(
  `partner-recent-${locale.value}-${post.value?.id ?? ''}`,
  () => getRecentPosts(post.value?.id),
  {
    server: true,
    lazy: false,
    default: () => [],
  },
);
</script>

<style lang="scss" module>
  .featTitle {
    color: #121E29;
    font-size: 32px;
    font-style: normal;
    font-weight: 300;
    line-height: normal;
    margin-bottom: 22px;
  }

  .featList {

    ul {
      display: flex;
      flex-direction: column;
      gap: 24px;
      padding: 0;
      margin: 0 0 25px 0;
      list-style: none;
    }

    li {
      position: relative;
      display: flex;
      align-items: center;
      padding: 0 0 0 7.5px;
      gap: 7.5px;

      &::before {
        content: '';
        flex-shrink: 0;
        width: 5px;
        height: 5px;
        border-radius: 5px;
        background-color: #293EFF;
      }
    }
  }
</style>
